export type NumberFormatter = Intl.NumberFormat['format'];

// initialize constructors with pre-specified options for consistency and performance
const makeNumberFormatter = (options?: object): NumberFormatter =>
  new Intl.NumberFormat('en-US', options).format;
const makeNumberFormatterFixed = (decimals: number) =>
  makeNumberFormatter({
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

export const formatNumber: NumberFormatter = makeNumberFormatter();
export const formatNumberFixed2: NumberFormatter = makeNumberFormatterFixed(2);
export const formatNumberFixed4: NumberFormatter = makeNumberFormatterFixed(4);
export const formatNumberCompact: NumberFormatter = makeNumberFormatter({
  notation: 'compact',
});

// Format a number with locale-aware thousands separators
// Example: formatNumber(1234567) → '1,234,567'
export const formatNumberWithSeparators = (value?: number | null): string => {
  if (value === undefined || value === null) return '';
  return value.toLocaleString();
};

// Format a ratio as a percentage string
// Example: formatRatioAsPercentage(0.123, 1) → '12.3%'
export const formatRatioAsPercentage = (
  value?: number | null,
  decimals = 1,
  fallback: string = '-',
): string => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return fallback;
  }
  return `${(value * 100).toFixed(decimals)}%`;
};

/**
 * Adaptive-precision formatter: integers render as locale-formatted integers,
 * values with magnitude ≥ 1 render with two fraction digits, and sub-unit
 * values render with three significant digits. Returns `fallback` for
 * `null`/`undefined`/non-finite inputs.
 */
export const formatNumberAdaptive = (
  value: number | null | undefined,
  fallback: string = '-',
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return fallback;
  }
  if (Number.isInteger(value)) return value.toLocaleString();
  if (Math.abs(value) >= 1) {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return value.toLocaleString(undefined, {
    minimumSignificantDigits: 3,
    maximumSignificantDigits: 3,
  });
};

export const formatNumberAdaptiveCompact = (
  value: number | null | undefined,
  fallback: string = '-',
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return fallback;
  }
  return Math.abs(value) >= 1e5
    ? formatNumberCompact(value)
    : formatNumberAdaptive(value, fallback);
};

export const formatNumberSignificant2 = (value: number): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '';
  }
  if (value === 0) {
    return '0';
  }

  const abs = Math.abs(value);

  if (abs >= 1) {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      notation: 'standard',
    }).format(value);
  }

  const leadingZeros = Math.max(0, -Math.floor(Math.log10(abs)) - 1);
  const maximumFractionDigits = Math.min(leadingZeros + 2, 20);

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits,
    notation: 'standard',
  }).format(value);
};
